import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  ArrowLeft,
  Lock,
  Settings,
  UserPlus,
  LogOut,
  Crown,
  MoreHorizontal,
  Heart,
  MessageCircle,
  Send,
  Camera,
  X,
  Trash2,
  Share2
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "./ui/dropdown-menu";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const groupTypes = {
  general: { label: 'General', icon: '⛳' },
  club: { label: 'Golf Club', icon: '🏌️' },
  league: { label: 'League', icon: '🏆' },
  friends: { label: 'Friends', icon: '👥' },
  work: { label: 'Work', icon: '💼' },
};

// Post Card Component
const GroupPostCard = ({ post, currentUser, onLike, onComment, onDelete }) => {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    await onComment?.(post.post_id, commentText);
    setCommentText('');
    setSubmitting(false);
  };

  const isAuthor = post.user_id === currentUser?.user_id;

  return (
    <div className="feed-post" data-testid={`group-post-${post.post_id}`}>
      {/* Header */}
      <div className="feed-post-header">
        <div className="feed-post-user">
          <Avatar className="w-10 h-10 ring-2 ring-[var(--ps-border)]">
            <AvatarImage src={post.user_picture} />
            <AvatarFallback className="bg-[var(--ps-bg-elevated)] text-[var(--ps-primary)]">
              {post.user_name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="feed-post-meta">
            <span className="feed-post-name">
              {post.user_name}
              {post.user_handicap !== null && (
                <span className="handicap-badge ml-2">{post.user_handicap} HCP</span>
              )}
            </span>
            <span className="text-xs text-[var(--ps-text-muted)]">
              {formatTimeAgo(post.created_at)}
            </span>
          </div>
        </div>
        {isAuthor && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2 rounded-full hover:bg-[var(--ps-bg-hover)] text-[var(--ps-text-muted)]">
                <MoreHorizontal className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)]">
              <DropdownMenuItem 
                className="text-red-500"
                onClick={() => onDelete?.(post.post_id)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Post
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Content */}
      <div className="feed-post-content">
        <p className="whitespace-pre-wrap">{post.content}</p>
      </div>

      {/* Image */}
      {post.image_url && (
        <img src={post.image_url} alt="Post" className="feed-post-image" loading="lazy" />
      )}

      {/* Reactions */}
      <div className="feed-post-reactions">
        <span>{post.likes_count || 0} like{post.likes_count !== 1 ? 's' : ''}</span>
        <button 
          className="hover:underline"
          onClick={() => setShowComments(!showComments)}
        >
          {post.comments_count || 0} comment{post.comments_count !== 1 ? 's' : ''}
        </button>
      </div>

      {/* Actions */}
      <div className="feed-post-actions">
        <button 
          className={`post-action-btn ${post.is_liked ? 'liked' : ''}`}
          onClick={() => onLike?.(post.post_id)}
        >
          <Heart className="w-5 h-5" />
          <span>Like</span>
        </button>
        <button 
          className="post-action-btn"
          onClick={() => setShowComments(!showComments)}
        >
          <MessageCircle className="w-5 h-5" />
          <span>Comment</span>
        </button>
      </div>

      {/* Comments */}
      {showComments && (
        <div className="p-4 border-t border-[var(--ps-border)] space-y-3">
          {post.comments?.map((comment) => (
            <div key={comment.comment_id} className="flex gap-3">
              <Avatar className="w-8 h-8 flex-shrink-0">
                <AvatarImage src={comment.user_picture} />
                <AvatarFallback className="bg-[var(--ps-bg-elevated)] text-[var(--ps-primary)] text-xs">
                  {comment.user_name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 bg-[var(--ps-bg-hover)] rounded-xl p-3">
                <span className="font-semibold text-sm text-[var(--ps-text-primary)]">
                  {comment.user_name}
                </span>
                <p className="text-sm text-[var(--ps-text-secondary)] mt-0.5">
                  {comment.content}
                </p>
              </div>
            </div>
          ))}

          {/* Add Comment */}
          <div className="flex gap-3 pt-2">
            <Avatar className="w-8 h-8 flex-shrink-0">
              <AvatarImage src={currentUser?.picture} />
              <AvatarFallback className="bg-[var(--ps-bg-elevated)] text-[var(--ps-primary)] text-xs">
                {currentUser?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 flex gap-2">
              <Input
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Write a comment..."
                className="dark-input flex-1 py-2 px-3 text-sm"
                onKeyPress={(e) => e.key === 'Enter' && handleSubmitComment()}
              />
              <Button
                onClick={handleSubmitComment}
                disabled={!commentText.trim() || submitting}
                className="dark-btn-primary py-2 px-3"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Create Post Form
const CreatePostForm = ({ user, onSubmit }) => {
  const [content, setContent] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        const MAX_WIDTH = 1200;
        
        if (width > MAX_WIDTH) {
          height = (height * MAX_WIDTH) / width;
          width = MAX_WIDTH;
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        setImage(dataUrl);
        setImagePreview(dataUrl);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!content.trim() && !image) return;
    
    setSubmitting(true);
    try {
      await onSubmit({ content, image_url: image });
      setContent('');
      setImage(null);
      setImagePreview(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dark-card p-4 mb-4">
      <div className="flex gap-3">
        <Avatar className="w-10 h-10 flex-shrink-0">
          <AvatarImage src={user?.picture} />
          <AvatarFallback className="bg-[var(--ps-bg-elevated)] text-[var(--ps-primary)]">
            {user?.name?.charAt(0) || 'U'}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Share something with the group..."
            className="dark-input min-h-[80px] resize-none"
          />
          
          {imagePreview && (
            <div className="relative mt-3">
              <img src={imagePreview} alt="Preview" className="max-h-40 rounded-lg" />
              <button
                onClick={() => { setImage(null); setImagePreview(null); }}
                className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          <div className="flex items-center justify-between mt-3">
            <label className="cursor-pointer flex items-center gap-2 text-[var(--ps-text-secondary)] hover:text-[var(--ps-primary)]">
              <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
              <Camera className="w-5 h-5" />
              <span className="text-sm">Add Photo</span>
            </label>
            <Button
              onClick={handleSubmit}
              disabled={(!content.trim() && !image) || submitting}
              className="dark-btn-primary"
            >
              {submitting ? <div className="dark-spinner w-4 h-4" /> : 'Post'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Member Item
const MemberItem = ({ member, isAdmin, canManage, onRemove, onPromote }) => {
  return (
    <div className="flex items-center justify-between py-3 border-b border-[var(--ps-border)] last:border-0">
      <div className="flex items-center gap-3">
        <Avatar className="w-10 h-10">
          <AvatarImage src={member.picture} />
          <AvatarFallback className="bg-[var(--ps-bg-elevated)] text-[var(--ps-primary)]">
            {member.name?.charAt(0) || 'U'}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="font-medium text-[var(--ps-text-primary)] flex items-center gap-2">
            {member.name}
            {member.role === 'admin' && (
              <Crown className="w-4 h-4 text-yellow-500" />
            )}
          </p>
          {member.handicap !== null && (
            <p className="text-xs text-[var(--ps-text-muted)]">{member.handicap} HCP</p>
          )}
        </div>
      </div>
      {canManage && member.role !== 'admin' && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-2 rounded-full hover:bg-[var(--ps-bg-hover)] text-[var(--ps-text-muted)]">
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)]">
            <DropdownMenuItem onClick={() => onPromote?.(member.user_id)}>
              <Crown className="w-4 h-4 mr-2" />
              Make Admin
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-red-500" onClick={() => onRemove?.(member.user_id)}>
              <Trash2 className="w-4 h-4 mr-2" />
              Remove
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

// Main Group Detail Page
export const GroupDetailPage = ({ user }) => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts'); // posts, members, about

  const isAdmin = group?.user_role === 'admin';
  const isMember = group?.is_member;
  const canView = group?.can_view !== false;

  // Fetch group
  const fetchGroup = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/groups/${groupId}`);
      setGroup(response.data);
      setPosts(response.data.posts || []);
    } catch (error) {
      toast.error("Failed to load group");
      navigate('/groups');
    } finally {
      setLoading(false);
    }
  }, [groupId, navigate]);

  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  // Join group
  const handleJoin = async () => {
    try {
      const response = await axios.post(`${API}/groups/${groupId}/join`);
      setGroup(response.data);
      toast.success("Joined group!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join");
    }
  };

  // Leave group
  const handleLeave = async () => {
    if (!window.confirm("Are you sure you want to leave this group?")) return;
    try {
      await axios.post(`${API}/groups/${groupId}/leave`);
      toast.success("Left group");
      navigate('/groups');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to leave");
    }
  };

  // Create post
  const handleCreatePost = async (data) => {
    try {
      const response = await axios.post(`${API}/groups/${groupId}/posts`, data);
      setPosts([response.data, ...posts]);
      toast.success("Posted!");
    } catch (error) {
      toast.error("Failed to create post");
    }
  };

  // Like post
  const handleLikePost = async (postId) => {
    try {
      const response = await axios.post(`${API}/groups/${groupId}/posts/${postId}/like`);
      setPosts(posts.map(p => 
        p.post_id === postId 
          ? { ...p, is_liked: response.data.liked, likes_count: response.data.likes_count }
          : p
      ));
    } catch (error) {
      toast.error("Failed to like post");
    }
  };

  // Comment on post
  const handleCommentPost = async (postId, content) => {
    try {
      const response = await axios.post(
        `${API}/groups/${groupId}/posts/${postId}/comment?content=${encodeURIComponent(content)}`
      );
      setPosts(posts.map(p => 
        p.post_id === postId 
          ? { 
              ...p, 
              comments: [...(p.comments || []), response.data],
              comments_count: (p.comments_count || 0) + 1
            }
          : p
      ));
    } catch (error) {
      toast.error("Failed to add comment");
    }
  };

  // Delete post
  const handleDeletePost = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/groups/${groupId}/posts/${postId}`);
      setPosts(posts.filter(p => p.post_id !== postId));
      toast.success("Post deleted");
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  // Remove member
  const handleRemoveMember = async (memberId) => {
    if (!window.confirm("Remove this member?")) return;
    try {
      await axios.delete(`${API}/groups/${groupId}/members/${memberId}`);
      fetchGroup();
      toast.success("Member removed");
    } catch (error) {
      toast.error("Failed to remove member");
    }
  };

  // Promote to admin
  const handlePromoteMember = async (memberId) => {
    try {
      await axios.patch(`${API}/groups/${groupId}/members/${memberId}/role?role=admin`);
      fetchGroup();
      toast.success("Member promoted to admin");
    } catch (error) {
      toast.error("Failed to promote member");
    }
  };

  // Share
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: group?.name,
        text: `Join ${group?.name} on Par Seeker!`,
        url: window.location.href
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied!");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="dark-spinner" />
      </div>
    );
  }

  if (!group) return null;

  const typeInfo = groupTypes[group.group_type] || groupTypes.general;

  return (
    <div className="pb-4" data-testid="group-detail-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[var(--ps-bg-base)] border-b border-[var(--ps-border)]">
        <div className="flex items-center justify-between p-4">
          <button 
            onClick={() => navigate('/groups')}
            className="flex items-center gap-2 text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Groups</span>
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={handleShare}
              className="p-2 rounded-full hover:bg-[var(--ps-bg-hover)] text-[var(--ps-text-secondary)]"
            >
              <Share2 className="w-5 h-5" />
            </button>
            {isAdmin && (
              <button className="p-2 rounded-full hover:bg-[var(--ps-bg-hover)] text-[var(--ps-text-secondary)]">
                <Settings className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Cover & Info */}
      {group.cover_image && (
        <div className="h-32 bg-[var(--ps-bg-surface)]">
          <img src={group.cover_image} alt={group.name} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="p-4 border-b border-[var(--ps-border)]">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-xl bg-[var(--ps-primary-light)] flex items-center justify-center text-3xl flex-shrink-0">
            {typeInfo.icon}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-[var(--ps-text-primary)]">{group.name}</h1>
              {group.is_private && <Lock className="w-4 h-4 text-[var(--ps-text-muted)]" />}
            </div>
            <p className="text-sm text-[var(--ps-text-muted)] mt-1">
              {typeInfo.label} • {group.member_count} member{group.member_count !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 flex gap-2">
          {!canView ? (
            <div className="flex-1 text-center py-3 bg-[var(--ps-bg-hover)] rounded-xl text-[var(--ps-text-muted)]">
              <Lock className="w-5 h-5 mx-auto mb-1" />
              <p className="text-sm">Private Group</p>
            </div>
          ) : isMember ? (
            <>
              {isAdmin && (
                <Button variant="ghost" className="flex-1 text-[var(--ps-text-secondary)]">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite
                </Button>
              )}
              <Button 
                variant="ghost" 
                className="text-red-500"
                onClick={handleLeave}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Leave
              </Button>
            </>
          ) : (
            <Button className="flex-1 dark-btn-primary" onClick={handleJoin}>
              <UserPlus className="w-4 h-4 mr-2" />
              Join Group
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      {canView && (
        <>
          <div className="flex gap-4 px-4 border-b border-[var(--ps-border)]">
            {['posts', 'members', 'about'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 text-sm font-medium capitalize transition-colors ${
                  activeTab === tab 
                    ? 'text-[var(--ps-primary)] border-b-2 border-[var(--ps-primary)]'
                    : 'text-[var(--ps-text-muted)] hover:text-[var(--ps-text-primary)]'
                }`}
              >
                {tab}
                {tab === 'members' && ` (${group.member_count})`}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-4">
            {activeTab === 'posts' && (
              <>
                {isMember && <CreatePostForm user={user} onSubmit={handleCreatePost} />}
                
                {posts.length === 0 ? (
                  <div className="dark-empty-state py-12">
                    <MessageCircle className="w-10 h-10 mb-3" />
                    <p className="text-[var(--ps-text-secondary)]">No posts yet</p>
                    {isMember && (
                      <p className="text-sm text-[var(--ps-text-muted)] mt-1">Be the first to post!</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {posts.map((post) => (
                      <GroupPostCard
                        key={post.post_id}
                        post={post}
                        currentUser={user}
                        onLike={handleLikePost}
                        onComment={handleCommentPost}
                        onDelete={handleDeletePost}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {activeTab === 'members' && (
              <div>
                {group.members?.map((member) => (
                  <MemberItem
                    key={member.user_id}
                    member={member}
                    isAdmin={member.role === 'admin'}
                    canManage={isAdmin && member.user_id !== user?.user_id}
                    onRemove={handleRemoveMember}
                    onPromote={handlePromoteMember}
                  />
                ))}
              </div>
            )}

            {activeTab === 'about' && (
              <div>
                {group.description ? (
                  <p className="text-[var(--ps-text-secondary)] whitespace-pre-wrap">
                    {group.description}
                  </p>
                ) : (
                  <p className="text-[var(--ps-text-muted)] italic">No description provided.</p>
                )}
                <div className="mt-4 pt-4 border-t border-[var(--ps-border)]">
                  <p className="text-sm text-[var(--ps-text-muted)]">
                    Created by {group.creator_name}
                  </p>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GroupDetailPage;
